import { useEffect, useRef, useState } from "react";
import client from "@/lib/api";
import { useUI } from "@/context/UIContext";
import { Loader2, RefreshCw } from "lucide-react";

const typing = () => {
  const el = document.activeElement;
  return !!el && (el.tagName === "INPUT" || el.tagName === "TEXTAREA" || el.tagName === "SELECT" || el.isContentEditable);
};
const getVersion = () => client.get("/version", { skipOutage: true }).then((r) => r.data.version);

export function SystemStatus() {
  const { bump } = useUI();
  const [updating, setUpdating] = useState(false);
  const [newVersion, setNewVersion] = useState(false);
  const [secs, setSecs] = useState(30);
  const known = useRef(null);
  const lastKey = useRef(0);

  useEffect(() => {
    const on = () => setUpdating(true);
    const key = () => { lastKey.current = Date.now(); };
    window.addEventListener("app:updating", on);
    window.addEventListener("keydown", key);
    getVersion().then((v) => { known.current = v; }).catch(() => {});
    const t = setInterval(() => getVersion().then((v) => {
      if (!known.current) known.current = v;
      else if (v !== known.current) setNewVersion(true);
    }).catch(() => {}), 60000);
    return () => { window.removeEventListener("app:updating", on); window.removeEventListener("keydown", key); clearInterval(t); };
  }, []);

  useEffect(() => {
    if (!updating) return undefined;
    const t = setInterval(() => getVersion().then((v) => {
      setUpdating(false);
      if (known.current && v !== known.current) setNewVersion(true);
      else bump();
    }).catch(() => {}), 5000);
    return () => clearInterval(t);
  }, [updating, bump]);

  useEffect(() => {
    if (!newVersion) return undefined;
    const t = setInterval(() => {
      if (typing() || Date.now() - lastKey.current < 10000) return;
      setSecs((s) => { if (s <= 1) window.location.reload(); return s - 1; });
    }, 1000);
    return () => clearInterval(t);
  }, [newVersion]);

  if (!updating && !newVersion) return null;
  return (
    <div className="sticky top-0 z-[60] flex flex-wrap items-center justify-center gap-3 bg-sky-600 px-4 py-2 text-sm font-medium text-white shadow" data-testid="system-status-bar">
      {newVersion ? (
        <>
          <RefreshCw className="h-4 w-4" />
          <span data-testid="system-new-version">A new version of DreamCRM is available. Refreshing in {secs}s{typing() ? " (paused while you type)" : ""}.</span>
          <button onClick={() => window.location.reload()} className="rounded-md bg-white px-3 py-1 text-xs font-semibold text-sky-700 hover:bg-sky-50" data-testid="system-refresh-now">Refresh now</button>
        </>
      ) : (
        <>
          <Loader2 className="h-4 w-4 animate-spin" />
          <span data-testid="system-updating">DreamCRM is updating – this usually takes under a minute. Your data will reload automatically.</span>
        </>
      )}
    </div>
  );
}
